import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, XIcon } from 'lucide-react';
import { ImageSlot } from './ImageSlot';
type LightboxProps = {
  images: string[];
  captions: string[];
  index: number | null;
  onClose: () => void;
  onChange: (index: number) => void;
};
const navBtn = 'flex h-12 w-12 items-center justify-center rounded-full border border-white/30 text-white transition-colors duration-150 ease-out hover:border-sun focus-visible:outline focus-visible:outline-2 focus-visible:outline-sun';
export function Lightbox({
  images,
  captions,
  index,
  onClose,
  onChange
}: LightboxProps) {
  const reduce = useReducedMotion();
  const closeRef = useRef<HTMLButtonElement>(null);
  const open = index !== null;
  const count = images.length;
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);
  useEffect(() => {
    if (index === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onChange((index + 1) % count);
      if (e.key === 'ArrowLeft') onChange((index - 1 + count) % count);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, count, onClose, onChange]);
  return <AnimatePresence data-id="element-112" data-mp="2hz9dmqputv4z">
      {index !== null && <motion.div role="dialog" aria-modal="true" aria-label={`Sideeksempel: ${captions[index]}`} className="fixed inset-0 z-[60] flex flex-col bg-ink/95 px-3 py-3" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: [0.23, 1, 0.32, 1]
    }} onClick={onClose} data-id="element-113" data-mp="2dypj7uwo15mj">
          <div className="flex items-center justify-between text-white" data-id="element-114" data-mp="z3zcrat3ntwh">
            <p className="text-[14px] font-medium" data-id="element-115" data-mp="1420w7wuftv8h">
              <span className="font-display font-extrabold text-sun" data-id="element-116" data-mp="13zoenksldmvy">
                {index + 1}/{count}
              </span>{' '}
              · {captions[index]}
            </p>
            <button ref={closeRef} type="button" onClick={onClose} aria-label="Lukk eksempelet" className="flex h-11 w-11 items-center justify-center rounded-full text-white transition-colors duration-150 ease-out hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sun" data-id="element-117" data-mp="u208zztg6uz0">
              <XIcon className="h-6 w-6" aria-hidden="true" data-id="element-118" data-mp="bdv76xwcpk2u" />
            </button>
          </div>

          {/* 9:16 page as tall as the screen allows, so the text can be read on a phone */}
          <div className="relative flex min-h-0 flex-1 items-center justify-center py-2" data-id="element-119" data-mp="2b3vcwzkcmao">
            <motion.div key={index} className="aspect-[9/16] h-full max-h-[86vh] max-w-full overflow-hidden rounded-xl bg-[#1B2233]" initial={reduce ? false : {
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.22,
          ease: [0.23, 1, 0.32, 1]
        }} onClick={e => e.stopPropagation()} data-id="element-120" data-mp="3ex2kpl2rgegl">
              <ImageSlot url={images[index]} alt={captions[index]} slotName={`pageImages[${index}]`} fit="contain" data-id="element-121" data-mp="1nwrnuh0ymn5" />
            </motion.div>
          </div>

          <div className="flex items-center justify-center gap-4" onClick={e => e.stopPropagation()} data-id="element-122" data-mp="bf134k7hrpd8">
            <button type="button" onClick={() => onChange((index - 1 + count) % count)} aria-label="Forrige side" className={navBtn} data-id="element-123" data-mp="6fo9ngm47k1o">
              <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-124" data-mp="kgij82kwfr1x" />
            </button>
            <button type="button" onClick={() => onChange((index + 1) % count)} aria-label="Neste side" className={navBtn} data-id="element-125" data-mp="fi787oivbai4">
              <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-126" data-mp="29wummjfk3mgo" />
            </button>
          </div>
        </motion.div>}
    </AnimatePresence>;
}